import { hasTriggerFormSubmit } from "@circle-react/helpers/hasTriggerFormSubmit";
import type { Editor } from "@tiptap/react";
import { useEffect } from "react";
import type { RefObject } from "react";
import { useHotkeys } from "react-hotkeys-hook";

import { useCommunityMemberReply } from "./useCommunityMemberReply";
import { useEditorFocus } from "./useEditorFocus";
import { useEditorState } from "./useEditorState";
import { useFocusChangeHandler } from "./useFocusChangeHandler";

interface UseEditorInteractionsProps {
  addToLocalSgidToObjectMap: (sgidToObjectMap: object) => void;
  autoFocus?: boolean;
  communityMemberForReply: any;
  currentCommunityMember: any;
  editorRef?: RefObject<Editor>;
  parentCommentId?: number;
  toolbarPortalId: string;
  usedIn: any;
}

export const useEditorInteractions = ({
  editorRef,
  autoFocus,
  parentCommentId,
  communityMemberForReply,
  currentCommunityMember,
  addToLocalSgidToObjectMap,
  usedIn,
  toolbarPortalId,
}: UseEditorInteractionsProps) => {
  const isUsedInMinimalPostModal = usedIn === "minimal-post-modal";
  const isReply = Boolean(parentCommentId);
  const isPostCommentFormInMinimalPostModal =
    isUsedInMinimalPostModal && !isReply;

  const {
    isEditorExpanded,
    editorKey,
    commentBoxRef,
    toolbarRef,
    formContext,
    isSubmitting,
    isSubmitDisabled,
    handleFocusChange,
    handleEditorFocus,
  } = useEditorState(isUsedInMinimalPostModal);

  const isSubmitShortcutEnabled = isPostCommentFormInMinimalPostModal
    ? isEditorExpanded && !isSubmitDisabled
    : !isSubmitDisabled;

  useEditorFocus(isPostCommentFormInMinimalPostModal, autoFocus, editorRef);

  useEffect(() => {
    toolbarRef.current = document.getElementById(toolbarPortalId);
  }, [toolbarPortalId, toolbarRef]);

  useFocusChangeHandler(
    isUsedInMinimalPostModal,
    toolbarRef,
    handleFocusChange,
  );

  useCommunityMemberReply(
    communityMemberForReply,
    currentCommunityMember,
    editorRef,
    addToLocalSgidToObjectMap,
  );

  useHotkeys(
    "meta+enter, ctrl+enter",
    () => {
      if (hasTriggerFormSubmit(formContext)) {
        formContext.triggerFormSubmit();
      }
    },
    {
      enableOnContentEditable: true,
      enabled: isSubmitShortcutEnabled,
    },
    [formContext, isSubmitShortcutEnabled],
  );

  return {
    isEditorExpanded,
    commentBoxRef,
    handleEditorFocus,
    editorKey,
    isSubmitDisabled,
    isSubmitting,
    isUsedInMinimalPostModal,
    isPostCommentFormInMinimalPostModal,
  };
};
